"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { Referral } from "@/src/models/documents/referral";

const EWEI_ASSIGNEE = "emily.fuentes@nvisioncenters.com";

interface EweiReferralInstructionsProps {
  referral: Referral;
  folder: string | null;
  assignedTo: string | null;
  workflow: string[];
  note: string | null;
  emailGated: boolean;
}

function WorkflowRow({ line }: { line: string }) {
  const colonIndex = line.indexOf(": ");

  if (colonIndex === -1) {
    return <div>{line}</div>;
  }

  const label = line.slice(0, colonIndex);
  const value = line.slice(colonIndex + 2);

  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      {value ? <CopyableContent text={value} /> : <span className="text-muted-foreground italic">Not provided</span>}
    </div>
  );
}

export function EweiReferralInstructions({
  referral,
  folder,
  assignedTo,
  workflow,
  note,
  emailGated,
}: EweiReferralInstructionsProps) {
  const taskWorkflow = workflow.filter((line) => !line.startsWith("Chart Note"));
  const chartNoteWorkflow = workflow.filter((line) => line.startsWith("Chart Note"));
  const hasTask = taskWorkflow.some((line) => line.startsWith("Task Type:"));

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-4 text-sm">
          <span className="font-medium text-center">EWEI NextGen Referral Instructions:</span>
          {emailGated ? (
            <p className="text-muted-foreground">
              This referral was routed by email to <span className="font-medium">{EWEI_ASSIGNEE}</span> — no document
              was filed in NextGen. Verify the email was received and take any necessary follow-up action.
            </p>
          ) : (
            <p className="text-muted-foreground">
              {hasTask
                ? "The document was automatically filed in NextGen and a task was created. Verify the values below match what was synced."
                : "The document was automatically filed in NextGen. Verify the values below and add the chart note manually."}
            </p>
          )}

          {!emailGated && (
            <div className="space-y-3">
              {folder && (
                <div>
                  <span className="text-muted-foreground">Folder: </span>
                  <CopyableContent text={folder} />
                </div>
              )}
              {taskWorkflow.map((line, index) => (
                <WorkflowRow key={index} line={line} />
              ))}
              {hasTask && assignedTo && (
                <div>
                  <span className="text-muted-foreground">Assigned To: </span>
                  <CopyableContent text={assignedTo} />
                </div>
              )}
              {chartNoteWorkflow.map((line, index) => (
                <WorkflowRow key={index} line={line} />
              ))}
              {note && <p className="text-muted-foreground italic">{note}</p>}
            </div>
          )}
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
